import { MediaProjectResponse } from "pages/media/types";

type MediaStatus = {
  isDraft: boolean;
  isPending: boolean;
  isInProgress: boolean;
  isReady: boolean;
  isGenerated: boolean;
  isError: boolean;
  isCancelled: boolean;
};

type StoryboardStatus = {
  isPending: boolean;
  isGenerating: boolean;
  isReviewing: boolean;
  isApproved: boolean;
  isError: boolean;
};

export const useProjectStatus = (
  project?: MediaProjectResponse,
  storyboardId?: string,
  instanceId?: string
) => {
  const media = instanceId
    ? project?.media.find((media) => media.instance_id === instanceId)
    : project?.media[0];
  const mediaStatus: MediaStatus = {
    isDraft: media?.status === "Draft",
    isPending: media?.status === "Pending",
    isInProgress: media?.status === "Generating",
    isReady: media?.status === "Ready",
    isGenerated: media?.status === "Generated",
    isError: media?.status === "Error",
    isCancelled: media?.status === "Cancelled",
  };
  const storyboard = storyboardId
    ? project?.storyboards.find((storyboard) => storyboard.id === storyboardId)
    : project?.storyboards[0];
  const storyboardStatus: StoryboardStatus = {
    isPending: storyboard?.status === "Pending",
    isGenerating: storyboard?.status === "Generating",
    isReviewing: storyboard?.status === "Reviewing",
    isApproved: storyboard?.status === "Approved",
    isError: storyboard?.status === "Error",
  };

  // The storyboard can fail either at the resource level (status "Error") or at
  // the project level ("StoryboardFailed"). Treat both as a generation failure.
  const isStoryboardError =
    storyboardStatus.isError || project?.status === "StoryboardFailed";

  const isMediaFinal =
    mediaStatus.isReady ||
    mediaStatus.isGenerated ||
    mediaStatus.isError ||
    mediaStatus.isCancelled;

  const isStoryboardFinal =
    storyboardStatus.isReviewing || storyboardStatus.isApproved;

  const projectStatus = () => {
    if (mediaStatus.isError) return "error";
    if (
      mediaStatus.isInProgress ||
      mediaStatus.isPending ||
      mediaStatus.isDraft
    )
      return "generating";
    if (isMediaFinal) return "ready";
    return "pending";
  };
  const isGeneratingMedia =
    (storyboardStatus.isApproved && mediaStatus.isInProgress) ||
    ((mediaStatus.isDraft || mediaStatus.isPending) &&
      storyboardStatus.isApproved);

  const isMediaError =
    mediaStatus.isError ||
    project?.status === "MediaFailed" ||
    project?.status === "SlideGenerationFailed";

  return {
    mediaStatus,
    storyboardStatus,
    isMediaFinal,
    isStoryboardFinal,
    isStoryboardError,
    isGeneratingMedia,
    isMediaError,
    projectStatus,
  };
};
